import React, { useContext, useState } from 'react';
import styled from 'styled-components';
import TopBackNavBar from '../../components/BackNavBar';
import BodyText from '../../components/BodyText';
import { PaymentInfo } from '../../interfaces/PaymentInfo';
import bookfair from '../../interfaces/bookfair-info';
import { formatCurrencyNumber } from '../../utils/amountFormat';
import { RedButton } from '../../components/Button';
import { Pages } from '../../App';
import TextInput from '../../components/TextInput';
import PaymentButton from '../../components/PaymentButton';
import { ConfigContext } from '../../providers/ConfigContext';
import useUpdateSessionInfo from '../../utils/updateSessionInfo';
import { setTaxErrorData } from '../../DumbleData';
import useMakeSale from '../../hooks/useMakeSale';
import { SaleResponseInfo } from '../../interfaces/sale-response-info';
import PaymentPayload from '../../interfaces/payment-payload';
import { BigNumber } from 'bignumber.js';

interface Props {
	pInfo: PaymentInfo;
	bfInfo: bookfair;
	isTaxExemptChecked: boolean;
	setTransactionId: any;
	setSaleResponseInfo: React.Dispatch<React.SetStateAction<SaleResponseInfo>>;
	setBookfairInfo: React.Dispatch<React.SetStateAction<bookfair>>;
	setCurrentPage: React.Dispatch<React.SetStateAction<number>>;
	setCashAmountPaid: React.Dispatch<React.SetStateAction<BigNumber>>;
}

enum PaymentMethods {
	STF,
	SD,
}
const CashPaymentContent = ({ pInfo, bfInfo, isTaxExemptChecked, setTransactionId, setSaleResponseInfo, setBookfairInfo, setCurrentPage, setCashAmountPaid }: Props) => {
	// State Hooks for Payment Amounts
	const [amountApplied, setAmountApplied] = useState(true);
	const [cashAmount, setCashAmount] = useState<string>(pInfo.amountDue.toFixed(2));
	const cashAmountVal = BigNumber(cashAmount);
	const remainingAmount = pInfo.amountDue.minus(cashAmountVal);
	const stfDisabled = remainingAmount.gt(bfInfo.stfBalance);
	const sdDisabled = cashAmountVal.lt(pInfo.tax);
	const sdTooltipText =
		'Unless the sale is fully paid with Scholastic Dollars, the cash amount must be greater than or equal to tax. Please adjust cash amount or select different second payment method.';

	// Determine Second Payment Method
	const getSecondPaymentMethod = () => {
		if (!stfDisabled) {
			return PaymentMethods.STF;
		} // Share the Fair
		if (!sdDisabled) {
			return PaymentMethods.SD;
		} // Scholastic Dollars
		return null;
	};
	const [selectedButton, setSelectedButton] = useState<number | null>(getSecondPaymentMethod());

	// API Variables
	const { apiBase } = useContext(ConfigContext);
	const [status, setStatus] = useState<number>(200);
	const [isButtonProcessing, setIsButtonProcessing] = useState(false);
	const { getSessionInfo } = useUpdateSessionInfo();
	const [makeSale] = useMakeSale();

	const handleClickApply = () => {
		if (!amountApplied && (cashAmountVal.lte(0) || !cashAmount)) {
			setCashAmount(pInfo.amountDue.toString());
		}
		// Auto-select the second payment method if the cash amount is less than the amount due
		setSelectedButton(getSecondPaymentMethod());
		setAmountApplied(!amountApplied);
	};

	// Handle cash amount input change
	const handleCashAmountChange = (value: any) => {
		let numericValue = value.replace(/[^\d.]/g, '');
		const match = numericValue.match(/^\d*\.?\d{0,2}$/);
		if (numericValue.startsWith('.')) {
			numericValue = '0' + numericValue;
		}
		if (value === '' || match) {
			setCashAmount(numericValue);
		}
	};

	// Submit transaction request for cash payment
	const handleCashPaymentSubmit = async () => {
		setIsButtonProcessing(true);
		const payload = makePayload();
		if (payload == null) {
			setIsButtonProcessing(false);
			return;
		}
		const { data, error } = await makeSale(payload);
		if (error) {
			if (error.status === 401) {
				setCurrentPage(Pages.Landing);
			}
			setIsButtonProcessing(false);
			setStatus(error.status);
			console.error(error);
		} else if (data != null) {
			setTransactionId(data.parentTransactionId);
			setCashAmountPaid(cashAmountVal);
			setSaleResponseInfo(data);
			window._satellite.track('cash-payment-submit');
			setCurrentPage(Pages.Confirmation);
		}
	};

	const makePayload = () => {
		const cashPaymentPayload: PaymentPayload = {
			total: {
				amount: pInfo.amountDue.toNumber(),
				tax: {
					amount: pInfo.tax.toNumber(),
					rate: bfInfo.taxRate.toNumber(),
					taxableSubtotal: isTaxExemptChecked ? 0 : pInfo.itemTotal.toNumber(),
					status: bfInfo.taxStatus,
					selectedExempt: isTaxExemptChecked,
				},
			},
			tenders: {
				cash: {
					amount: cashAmountVal.gt(pInfo.amountDue) ? pInfo.amountDue.toNumber() : cashAmountVal.toNumber(),
				},
			},
		};

		if (cashAmountVal.lt(pInfo.amountDue)) {
			// Use the second selected payment method to pay the remaining amount (selectedButton)
			switch (selectedButton) {
				case PaymentMethods.STF:
					// Use Share the Fair
					cashPaymentPayload.tenders.stf = {
						amount: remainingAmount.toNumber(),
					};
					break;
				case PaymentMethods.SD:
					// Use Scholastic Dollars
					cashPaymentPayload.tenders.sd = {
						amount: remainingAmount.toNumber(),
					};
					break;
				default:
					// Throw Error (should never happen)
					console.error('No second payment method selected');
					return null;
			}
		}
		return cashPaymentPayload;
	};

	// If there is a mismatch with tax values, retrieve up-to-date tax info and send to new purchase page
	const handleTaxDiscrepancy = async () => {
		const success = await getSessionInfo(apiBase, setBookfairInfo);
		if (success) {
			setCurrentPage(Pages.NewPurchase);
		} else {
			setCurrentPage(Pages.Landing);
		}
	};

	// Determine error message based on error code
	let errorMessage: React.ReactNode = null;
	if (status === 422) {
		setTaxErrorData(true);
		errorMessage = (
			<>
				{'There appears to be a discrepancy in the sales tax amount. In order to proceed, please '}
				<StyledLink onClick={handleTaxDiscrepancy}>click here</StyledLink>
				{' to recalculate the sales tax.'}
			</>
		);
	} else if (status !== 200) {
		errorMessage = 'We are unable to process your cash order. Please use a different payment method or call 877-245-0903.';
	}

	// Disable submit button if no amount is applied OR if cash amount is less than amount due and both payment methods are disabled OR if button is processing OR if status is not 200
	const isSubmitDisabled = !amountApplied || (cashAmountVal.lt(pInfo.amountDue) && selectedButton === null) || isButtonProcessing || status !== 200;

	return (
		<>
			<TopBackNavBar setCurrentPage={setCurrentPage} title="Input Payment Amount" location={Pages.PaymentMethod} />
			<STFRedeemPage>
				<ContentWrapper>
					<CalculationsDiv>
						<RowItemDiv>
							<ItemLabel>Item Total</ItemLabel>
							<ItemValue>{formatCurrencyNumber(pInfo.itemTotal.toNumber())}</ItemValue>
						</RowItemDiv>
						<HorizontalLine />
						<RowItemDiv>
							<ItemLabel>Tax</ItemLabel>
							<ItemValue>{formatCurrencyNumber(pInfo.tax.toNumber())}</ItemValue>
						</RowItemDiv>
						<HorizontalLine />
						<RowItemDiv>
							<ItemLabel>Amount Due</ItemLabel>
							<ItemValue>{formatCurrencyNumber(pInfo.amountDue.toNumber())}</ItemValue>
						</RowItemDiv>
						<HorizontalLine />
						{amountApplied ? (
							<RowItemDiv>
								<ItemLabel>Cash Payment</ItemLabel>
								<ItemValue>{formatCurrencyNumber(cashAmountVal.toNumber())}</ItemValue>
							</RowItemDiv>
						) : (
							<StyledInputDiv>
								<TextInput label="Cash Payment" value={cashAmount} inputAlign="right" onChange={handleCashAmountChange} />
							</StyledInputDiv>
						)}
						<StyledRightAlignDiv>
							<StyledButton onClick={handleClickApply}>{amountApplied ? 'Edit Amount' : 'Apply Amount'}</StyledButton>
						</StyledRightAlignDiv>
						{amountApplied && pInfo.amountDue.gt(cashAmountVal) && (
							<>
								<HorizontalLine />
								<RowItemDiv>
									<ItemLabel>Please choose another payment to process the remaining</ItemLabel>
									<ItemValue>{formatCurrencyNumber(remainingAmount.toNumber())}</ItemValue>
								</RowItemDiv>
								<AlternativePaymentsDiv>
									<PaymentButton disabled={stfDisabled} onClick={() => setSelectedButton(PaymentMethods.STF)} isSelected={selectedButton === PaymentMethods.STF}>
										<ShareTheFairDiv>
											<InnerText>Share the Fair</InnerText>
											<FineInnerText>Balance: {formatCurrencyNumber(bfInfo.stfBalance.toNumber())}</FineInnerText>
										</ShareTheFairDiv>
									</PaymentButton>
									<PaymentButton
										disabled={sdDisabled}
										onClick={() => setSelectedButton(PaymentMethods.SD)}
										isSelected={selectedButton === PaymentMethods.SD}
										tooltipText={sdTooltipText}
										tooltipEnabled={sdDisabled}
									>
										<InnerText>Scholastic Dollars</InnerText>
									</PaymentButton>
								</AlternativePaymentsDiv>
							</>
						)}
						<StyledRedButton disabled={isSubmitDisabled} handleClick={handleCashPaymentSubmit}>
							Submit Payment
						</StyledRedButton>
						{errorMessage && <ErrorText>{errorMessage}</ErrorText>}
					</CalculationsDiv>
				</ContentWrapper>
			</STFRedeemPage>
		</>
	);
};

const STFRedeemPage = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
`;

const ContentWrapper = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	width: 280px;
	margin-top: 53px;
`;

const CalculationsDiv = styled.div`
	width: 100%;
	display: flex;
	flex-direction: column;
	align-items: flex-start;
	gap: 12px;
	margin-bottom: 38px;
`;

const RowItemDiv = styled.div`
	display: flex;
	width: 100%;
	justify-content: space-between;
	align-items: center;
`;

const ItemLabel = styled(BodyText)`
	text-align: left;
	font-size: 16px;
	font-weight: 500;
	line-height: 19px;
	margin: 0;
`;

const ItemValue = styled(BodyText)`
	text-align: right;
	font-size: 21px;
	font-weight: 500;
	line-height: 24px;
	margin: 0;
`;

const HorizontalLine = styled.div`
	width: 100%;
	height: 1px;
	background: #d7d7d7;
	margin: 0;
`;

const StyledRedButton = styled(RedButton)`
	width: 100%;
	margin-top: 27px;
`;

const StyledInputDiv = styled.div`
	width: 100%;
`;

const StyledRightAlignDiv = styled.div`
	width: 100%;
	text-align: right;
`;

const StyledButton = styled.button`
	background: none;
	border: none;
	padding: 0;
	margin: 0;
	cursor: pointer;
	color: red;
	font-size: 11px;
	font-weight: 600;
	letter-spacing: 1.5px;
	text-transform: uppercase;
	&:active {
		color: #660000;
	}
`;

const AlternativePaymentsDiv = styled.div`
	display: flex;
	flex-direction: column;
	align-items: flex-start;
	gap: 20px;
	width: 100%;
	margin-top: 28px;
`;

const InnerText = styled.p`
	font-size: 16px;
	font-weight: 400;
	line-height: 19px;
	color: #e81111;
	margin: 10px 0;
	text-align: left;
`;

const FineInnerText = styled.p`
	font-size: 13px;
	font-weight: 500;
	line-height: 15px;
	color: #333333;
	margin: 0;
`;

const ShareTheFairDiv = styled.div`
	display: flex;
	flex-direction: column;
	justify-content: center;
	align-items: flex-start;
	gap: 6px;
	flex: 1 0 0;
	${InnerText} {
		margin: 0px;
	}
`;

const ErrorText = styled(BodyText)`
	font-size: 13px;
	margin-top: 10px;
	text-align: left;
`;
const StyledLink = styled.a`
	text-decoration: underline;
	cursor: pointer;
`;

export default CashPaymentContent;
